import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Paper
} from '@mui/material';

interface ResponseDetailModalProps {
  open: boolean;
  onClose: () => void;
  response: any;
}

const ResponseDetailModal: React.FC<ResponseDetailModalProps> = ({ open, onClose, response }) => {
  if (!response) return null;

  const formatDate = (date: string | undefined | null) => {
    if (!date) return '-';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '-';
      return dateObj.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">응답 상세 정보</Typography>
          <Chip 
            label={response.status === 'completed' ? '완료' : response.status === 'in_progress' ? '진행 중' : '포기'}
            color={response.status === 'completed' ? 'success' : response.status === 'in_progress' ? 'warning' : 'error'}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            기본 정보
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Box>
                <Typography variant="caption" color="textSecondary">응답 ID</Typography>
                <Typography variant="body2">{response.id}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">설문 제목</Typography>
                <Typography variant="body2">{response.surveyTitle || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">응답자</Typography>
                <Typography variant="body2">{response.respondentName || response.respondentId}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">제출 시간</Typography>
                <Typography variant="body2">
                  {formatDate(response.submittedAt || response.completedAt || response.startedAt)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {response.metadata && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              디바이스 정보
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
                <Box>
                  <Typography variant="caption" color="textSecondary">디바이스</Typography>
                  <Typography variant="body2">{response.metadata.deviceType || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">브라우저</Typography>
                  <Typography variant="body2">{response.metadata.browser || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">운영체제</Typography>
                  <Typography variant="body2">{response.metadata.os || '-'}</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            응답 내용 ({response.answers?.length || 0}개 질문)
          </Typography>
          <Paper sx={{ p: 2 }}>
            {response.answers && response.answers.length > 0 ? (
              response.answers.map((answer: any, index: number) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    질문 {index + 1} (ID: {answer.questionId})
                  </Typography>
                  <Box sx={{ p: 1.5, backgroundColor: '#fafafa', borderRadius: 1 }}>
                    <Typography variant="body1">
                      {typeof answer.value === 'object' 
                        ? JSON.stringify(answer.value, null, 2)
                        : answer.value}
                    </Typography>
                  </Box>
                  {index < response.answers.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                응답 내용이 없습니다.
              </Typography>
            )}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResponseDetailModal;